import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Group } from '@mui/icons-material';
import { deepPurple, green, grey } from '@mui/material/colors';
import Navbar from '../components/Navbar';
import SurveyCreatorWidget from '../components/SurveyCreatorWidget';
import { ourSurveyToSurveyJS, surveyJSToOurSurvey } from '../utils/surveyJsonConverter';
import {
  getSurveys,
  createSurvey,
  updateSurvey,
  deleteSurvey,
  getSurveyAccess,
  setSurveyAccess,
  getRampProjects,
} from '../services/surveyService';

export default function SurveyList() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [openAccessDialog, setOpenAccessDialog] = useState(false);
  const [accessSurvey, setAccessSurvey] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const creatorRef = useRef(null);
  const [surveyStatus, setSurveyStatus] = useState('draft');

  useEffect(() => {
    const loadSurveys = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getSurveys();
        setSurveys(data || []);
      } catch (e) {
        setError(e.message || 'Failed to load surveys.');
      } finally {
        setLoading(false);
      }
    };

    const loadProjects = async () => {
      setProjectsLoading(true);
      setProjectsError('');
      try {
        const projectIds = await getRampProjects();
        setProjectOptions((projectIds || []).map((id) => Number(id)));
      } catch {
        setProjectsError('Failed to load project IDs.');
      } finally {
        setProjectsLoading(false);
      }
    };
    loadSurveys();
    loadProjects();
  }, []);

  useEffect(() => {
    if (!accessSurvey) {
      setSelectedProjectIds([]);
      return;
    }
    const initialSelected = (accessSurvey.project_ids || []).map((id) => Number(id));
    setSelectedProjectIds(initialSelected);
  }, [accessSurvey]);

  const filteredSurveys = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return surveys;
    return surveys.filter((s) => {
      const projects = (s.project_ids || []).join(' ').toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q) ||
        projects.includes(q)
      );
    });
  }, [search, surveys]);

  const handleCreateClick = () => {
    creatorRef.current = null;
    setSurveyStatus('draft');
    setEditingSurvey({
      id: null,
      name: '',
      description: '',
      status: 'draft',
      project_ids: [],
      questions: [],
    });
    setOpenEditDialog(true);
  };

  const handleEditClick = (survey) => {
    creatorRef.current = null;
    setSurveyStatus(survey.status || 'draft');
    setEditingSurvey({
      ...survey,
      project_ids: survey.project_ids || [],
      questions: survey.questions || [],
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (survey) => {
    if (!window.confirm(`Delete survey "${survey.name}"? This cannot be undone.`)) return;
    (async () => {
      try {
        await deleteSurvey(survey.id);
        const data = await getSurveys();
        setSurveys(data || []);
      } catch (e) {
        alert(e.message || 'Failed to delete survey.');
      }
    })();
  };

  const handleAccessClick = (survey) => {
    setAccessSurvey(survey);
    setOpenAccessDialog(true);
    (async () => {
      try {
        const projectIds = await getSurveyAccess(survey.id);
        setSelectedProjectIds(projectIds.map((id) => Number(id)));
      } catch {
        // ignore; dialog will show no pre-selected project ids
      }
    })();
  };

  const handleToggleProject = (projectId) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const handleSaveAccess = () => {
    if (!accessSurvey) return;
    const projectIds = [...selectedProjectIds];

    (async () => {
      try {
        await setSurveyAccess(accessSurvey.id, projectIds);
        setSurveys((prev) =>
          prev.map((s) =>
            s.id === accessSurvey.id
              ? { ...s, project_ids: projectIds }
              : s
          )
        );
        setAccessSurvey((prev) =>
          prev ? { ...prev, project_ids: projectIds } : prev
        );
        setOpenAccessDialog(false);
      } catch (e) {
        alert(e.message || 'Failed to save access.');
      }
    })();
  };

  const handleSaveSurvey = () => {
    if (!editingSurvey) return;
    const creator = creatorRef.current;
    if (!creator || !creator.JSON) {
      alert('Survey builder is not ready. Please wait or try again.');
      return;
    }
    const payload = surveyJSToOurSurvey(creator.JSON, { ...editingSurvey, status: surveyStatus });
    if (!payload.name?.trim()) {
      alert('Survey name is required. Set the survey title in the builder.');
      return;
    }

    (async () => {
      try {
        if (payload.id != null) {
          await updateSurvey(payload.id, payload);
        } else {
          const { id, ...createPayload } = payload;
          await createSurvey(createPayload);
        }
        const data = await getSurveys();
        setSurveys(data || []);
        setOpenEditDialog(false);
        setEditingSurvey(null);
        creatorRef.current = null;
      } catch (e) {
        alert(e.message || 'Failed to save survey.');
      }
    })();
  };

  const statusColor = (status) => {
    const value = (status || '').toLowerCase();
    if (value === 'active') return green[600];
    if (value === 'draft') return grey[700];
    return deepPurple[500];
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ pt: 10, mt: 4, mb: 6, flex: 1, pb: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: deepPurple[800] }}>
                Surveys
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage survey templates, access, and availability.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateClick}
              sx={{
                bgcolor: deepPurple[600],
                '&:hover': { bgcolor: deepPurple[700] },
                borderRadius: '999px',
                textTransform: 'none',
                px: 2.5,
              }}
            >
              Create New Survey
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              label="Search surveys"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: deepPurple[500] }} />
            </Box>
          ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: deepPurple[50] }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Access</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSurveys.map((survey) => (
                  <TableRow key={survey.id} hover>
                    <TableCell>{survey.name}</TableCell>
                    <TableCell>{survey.description || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={(survey.status || '').toLowerCase() || 'draft'}
                        sx={{
                          backgroundColor: `${statusColor(survey.status)}20`,
                          color: statusColor(survey.status),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={`${(survey.project_ids || []).length} project(s)`}
                          variant="outlined"
                          sx={{ textTransform: 'none' }}
                        />
                        {survey.project_ids && survey.project_ids.length > 0 && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {survey.project_ids.join(', ')}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit survey">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditClick(survey)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage access">
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => handleAccessClick(survey)}
                          >
                            <Group fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete survey">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(survey)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSurveys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                        No surveys found. Click &quot;Create New Survey&quot; to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      </Container>

      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setEditingSurvey(null);
          creatorRef.current = null;
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: 900 } }}
      >
        <DialogTitle>{editingSurvey?.id ? 'Edit Survey' : 'Create Survey'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {editingSurvey && (
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={surveyStatus}
                  onChange={(e) => setSurveyStatus(e.target.value)}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                Drag questions from the left panel onto the canvas. Use the survey title and description in the builder (click the survey root in the tree).
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <SurveyCreatorWidget
                  key={editingSurvey.id ?? 'new'}
                  initialSurveyJsJson={ourSurveyToSurveyJS(editingSurvey)}
                  onCreatorReady={(cr) => {
                    creatorRef.current = cr;
                  }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setEditingSurvey(null);
              creatorRef.current = null;
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSurvey}
            sx={{ bgcolor: deepPurple[600], '&:hover': { bgcolor: deepPurple[700] } }}
          >
            Save survey
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAccessDialog}
        onClose={() => {
          setOpenAccessDialog(false);
          setAccessSurvey(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Survey Access</DialogTitle>
        <DialogContent dividers>
          {accessSurvey && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {accessSurvey.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Access is based on project IDs.
              </Typography>
              <Typography variant="subtitle2">Select project IDs with access</Typography>
              {projectsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: deepPurple[500] }} />
                </Box>
              ) : projectsError ? (
                <Typography variant="body2" color="error">
                  {projectsError}
                </Typography>
              ) : projectOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No project IDs available.
                </Typography>
              ) : (
                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: grey[300],
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                  }}
                >
                  {projectOptions.map((projectId) => (
                    <FormControlLabel
                      key={projectId}
                      control={
                        <Checkbox
                          checked={selectedProjectIds.includes(projectId)}
                          onChange={() => handleToggleProject(projectId)}
                          size="small"
                        />
                      }
                      label={`Project ${projectId}`}
                      sx={{ m: 0, py: 0.5, width: '100%' }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                Project IDs are fetched from RAMP and saved on this survey.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAccessDialog(false);
              setAccessSurvey(null);
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAccess}
            sx={{ bgcolor: deepPurple[600], '&:hover': { bgcolor: deepPurple[700] } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

